import React from 'react';
import { useState } from 'react';
import {
  CcfAgentSettingsSendIcon,
  CcfSwitchItem,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { LocalStorageHelper, NotificationSettings } from '@nice-ccf/core-sdk';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import KeyboardIcon from "@mui/icons-material/Keyboard";

import {
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import styles from './ccf-display-and-accessability.module.scss';
import Paper from '@mui/material/Paper';

/**
 * @example CcfDisplayAndAccessability()
 * @returns
 */
export function CcfDisplayAndAccessability() {
  const [twentyFourHourTime, setTwentyFourHourTime] = useState(false);
  const [panelPopout, setPanelPopout] = useState(false);
  const [pageActionOnly, setPageActionOnly] = useState(true);
  const [channels, setChannels] = useState('All channels except email');
  const [translate] = useTranslator();

  /**
   *
   * @param event - switch click event
   * @param checked - switch boolean
   * @example handleChange
   */
  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    const target = event.target as HTMLInputElement;

    LocalStorageHelper.setItem(target.name, checked);

    switch (target.name) {
      case NotificationSettings.TWENTY_FOUR_HOUR_TIME:
        setTwentyFourHourTime(checked);
        break;
      case NotificationSettings.PANEL_POPOUT:
        setPanelPopout(checked);
        break;
      case NotificationSettings.PANEL_POPOUT_PAGE_ACTION_ONLY:
        setPageActionOnly(checked);
        break;
      default:
        break;
    }
  };

  /**
   * Handles the change event for the channel selection dropdown.
   * @param event - The change event triggered by the dropdown.
   * @example handleChannelChange
   */
  const handleChannelChange = (event: any) => {
    setChannels(event.target.value);
  };
 /**
   * Handles the createData for the shortcuts table.
   * @param event - The change event triggered by the dropdown.
   * @example createData
   */
  function createData(name: string, shortcut: string) {
    return { name, shortcut };
  }

  const rows = [
    createData('Top Navigation Shortcuts', ''),
    createData('Focus Top Navigation', 'Ctrl + Alt + T'),
    createData('Open CX Help Page', 'Ctrl + F2'),
    createData('Open Notifications', 'Ctrl + Alt + N'),
    createData('Connect Agent Voice Link (Agent Leg)', 'Ctrl + Alt + C'),
    createData('Focus Agent States', 'Ctrl + Alt + O'),
    createData('Quick Bar Shortcuts', ''),
    createData('Focus Quick Bar', 'Ctrl + Alt + B'),
    createData('Go To Home (Contact History)', 'Ctrl + Shift + H'),
    createData('Open Interaction Search', 'Ctrl + Shift + 1'),
    createData('Open Queue Counter', 'Ctrl + Shift + Q'),
    createData('Open Directory', 'Ctrl + Shift + Z'),
    createData('Open Schedule', 'Ctrl + Shift + X'),
    createData('Open WEM', 'Ctrl + Shift + 2'),
    createData('Open Settings', 'Ctrl + Shift + 4'),
    createData('Open Custom Workspace', 'Ctrl + Shift + K'),
    createData('Open Reporting', 'Ctrl + Shift + 5'),
    createData('Open Launch', 'Ctrl + Shift + 6'),
    createData('Open Customer Card', 'Ctrl + Shift + U'),
    createData('Open Multiparty', 'Ctrl + Shift + V'),
    createData('Open CoPilot', 'Ctrl + Alt + Y'),
    createData('Open Quick Response', 'Ctrl + Alt + 3'),
    createData('Open Screen Pop', 'Ctrl + Alt + F'),
    createData('Open More Menu', 'Ctrl + Alt + M'),
    createData('Phone Control Shortcuts', ''),
    createData('Accept Incoming Call', 'Ctrl + Alt + A'),
    createData('Reject Incoming Call', 'Ctrl + Alt + R'),
    createData('Call Hold/Unhold', 'Alt + H'),
    createData('Call Mute/Unmute', 'Alt + M'),
    createData('Call Mask/Unmask', 'Alt + N'),
    createData('Call Record', 'Alt + R'),
    createData('Open Dialpad', 'Alt + K'),
    createData('Go Back To Phone Controls From Dialpad', 'Alt + L'),
    createData('Hang Up', 'Alt + '),
    createData('General Shortcuts', ''),
    createData('Open Outcome Panel (Disposition)', 'Ctrl + Alt + D'),
    createData(
      'Switch Tabs / Go To Next Tab (Settings, Reporting, Interaction Menu)',
      'Ctrl + Alt + → (Right Arrow Key)'
    ),
    createData(
      'Switch Tabs / Go To Previous Tab (Settings, Reporting, Interaction Menu)',
      'Ctrl + Alt + ← (Left Arrow Key)'
    ),
    createData('Switch Digital Interactions', 'Ctrl + Alt + I'),
  ];

  return (
    <div style={{ height: '100%' }}>
      <List
        sx={{ width: '100%', bgcolor: 'background.paper', marginBottom: '0.7rem' }}
        subheader={<ListSubheader>{translate('display')}</ListSubheader>}
      >
        <CcfSwitchItem
          handleChange={handleChange}
          icon={<AccessTimeIcon />}
          id={'twentyFourHourTime'}
          isChecked={twentyFourHourTime}
          name={translate(NotificationSettings.TWENTY_FOUR_HOUR_TIME)}
          text="twentyFourHourTime"
        />
        <CcfSwitchItem
          handleChange={handleChange}
          // hideLessThanExtraLarge={true}
          icon={<OpenInBrowserIcon />}
          isChecked={panelPopout}
          name={translate(NotificationSettings.PANEL_POPOUT)}
          text="panelOpenInBrowser"
        />
        <CcfSwitchItem
          handleChange={handleChange}
          // hideLessThanExtraLarge={true}
          icon={<OpenInBrowserIcon />}
          isChecked={pageActionOnly}
          name={translate(NotificationSettings.PANEL_POPOUT_PAGE_ACTION_ONLY)}
          text="panelOpenInBrowserPageActionOnly"
        />
      </List>
      <div className={styles.keyboardBlock}>
        <Typography
          variant="body1"
          sx={{ fontSize: '1rem', lineHeight: '1.5', fontWeight: 700 }}
        >
          {translate('keyboard')}
        </Typography>
      </div>
      <div className={styles.keyboardBlock}>
        <div className={styles.keyboardBlockText}>
          <CcfAgentSettingsSendIcon />
          <Typography
            id="sendWithEnter-label"
            variant="body1"
            sx={{
              marginRight: '3.5rem',
              fontWeight: 500,
              color: 'rgb(97, 108, 117)',
              marginLeft: '5px',
            }}
          >
            Send with Enter
          </Typography>
          <FormControl
            fullWidth
            variant="outlined"
            className={styles.channelBlock}
            size="small"
          >
            <Select
              labelId="category-select-label"
              id="category-select"
              value={channels}
              onChange={handleChannelChange}
              displayEmpty
            >
              <MenuItem value="All channels">All channels</MenuItem>
              <MenuItem value="All channels except email">
                All channels except email
              </MenuItem>
              <MenuItem value="sound-delay">No channels</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className={styles.keyboardBlockText}>
          <KeyboardIcon style={{ fill: 'rgb(150, 150, 150)' }} />
          <Typography
            id="sendWithEnter-label"
            variant="body1"
            sx={{
              marginRight: '3.5rem',
              fontWeight: 500,
              color: 'rgb(97, 108, 117)',
              marginLeft: '5px',
            }}
          >
            Shortcuts
          </Typography>
        </div>
        <div className={styles.tableBlock}>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="sticky table">
                <TableHead sx={{ backgroundColor: 'white', borderBottom: '0.1875rem solid rgb(234, 240, 246) !important',borderTop:'none' }}>
                <TableRow>
                <TableCell className={styles.tableHead}>ACTION</TableCell>
                <TableCell className={styles.tableHead} align="right">SHORTCUT</TableCell>
                </TableRow>
                </TableHead>
              <TableBody>
              {rows.map((row) => (
                <TableRow
                key={row.name}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                <TableCell 
                  component="td" 
                  scope="row" 
                  sx={{ width: '50%' }} 
                  className={styles.tableRow} 
                  style={{ 
                  fontWeight: row.shortcut === '' ? 700 : 400, 
                  }}
                >
                  {row.name}
                </TableCell>
                <TableCell align="right" sx={{ width: '50%' }} className={styles.tableRow}>
                  {row.shortcut}
                </TableCell>
                </TableRow>
              ))}
              </TableBody>
            </Table>
            </TableContainer>
        </div>
      </div>
    </div>
  );
}

export default CcfDisplayAndAccessability;
